'use strict'

// Formats server-reported meta.splitCollisions (see
// server/src/hmi/routeOptimizer/territoryProperties.controller.js:
// enforceSplitSiblingSeparation) into display lines for the Refine Route
// warning banner. Each collision entry is one persistent split-sibling
// problem the analysis guard could not (or would only forcibly) resolve:
//   - 'unresolvable': siblings still share a (crew, weekType, dayOfWeek)
//     because Aspire's own routes map to the same crew, or the source day
//     isn't bucketable (weekend).
//   - 'guard_moved_locked_sibling': the guard had to re-home a LOCKED or
//     manually-overridden sibling to clear the collision — both siblings
//     had deliberate placements, so the forced move is surfaced for review.
//
// `propertyNameById` is an optional Map/plain-object keyed by aspirePropertyId
// (Number or String) -> propertyName, used to prefix lines when a cheap
// lookup is available. Falls back to "Ticket {id}" when no name is found.
export const formatSplitCollisionLine = (entry, propertyNameById) => {
  if (!entry) return ''
  const lookup = propertyNameById && typeof propertyNameById.get === 'function'
    ? propertyNameById.get(Number(entry.aspirePropertyId))
    : propertyNameById && entry.aspirePropertyId != null
      ? propertyNameById[entry.aspirePropertyId]
      : null
  const label = lookup ? `${lookup} — Ticket ${entry.workTicketId}` : `Ticket ${entry.workTicketId}`

  if (entry.reason === 'guard_moved_locked_sibling') {
    return `${label} — a locked/overridden sibling was moved from ${entry.fromDayOfWeek} to ${entry.dayOfWeek} to separate the pair`
  }
  // Default / 'unresolvable'
  return `${label} — siblings share ${entry.dayOfWeek} (wk ${entry.weekType}) on one crew (Aspire routes map to the same crew)`
}

// Distinct-ticket count for the banner headline.
export const countDistinctSplitCollisionTickets = (collisions) => {
  if (!Array.isArray(collisions) || collisions.length === 0) return 0
  return new Set(collisions.map(entry => entry.workTicketId)).size
}

// Formatted line list: ONE line per distinct workTicketId, in first-seen
// order, so the list length always matches the headline's distinct-ticket
// count. A 3-/4-way split ticket can report several collision entries; the
// first entry formats the line and the rest collapse into a
// " (+N more collisions)" suffix.
export const formatSplitCollisions = (collisions, propertyNameById) => {
  if (!Array.isArray(collisions)) return []
  const extraByTicket = new Map() // workTicketId -> count of entries beyond the first
  const lineIndexByTicket = new Map()
  const lines = []
  for (const entry of collisions) {
    const ticketId = entry?.workTicketId
    if (!lineIndexByTicket.has(ticketId)) {
      lineIndexByTicket.set(ticketId, lines.length)
      extraByTicket.set(ticketId, 0)
      lines.push(formatSplitCollisionLine(entry, propertyNameById))
    } else {
      extraByTicket.set(ticketId, extraByTicket.get(ticketId) + 1)
    }
  }
  for (const [ticketId, extra] of extraByTicket.entries()) {
    if (extra > 0) {
      const index = lineIndexByTicket.get(ticketId)
      lines[index] = `${lines[index]} (+${extra} more collision${extra === 1 ? '' : 's'})`
    }
  }
  return lines
}
